import PropTypes from 'prop-types';
import { useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { ChevronDownIcon, ChevronRightIcon, CircleStackIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import Tooltip from '../core/Tooltip';
import ConfigurationChangeModal from './ConfigurationChangeModal';
import SaveConfigurationModal from './SaveConfigurationModal';
import ManageConfigurationsModal from './ManageConfigurationsModal';
import { useCaseNode, nodeActions, useIsDefault } from '@/stores/nodeStore';
import { caseActions } from '@/stores/caseStore';
import { patchNode, createParamConfiguration, deleteParamConfiguration, updateParamConfiguration } from '@/api/nodes';
import FloppyDiskIcon from '@/assets/floppy-disk.svg';

function ContinentSubsection({ continent, options, selectedValue, defaultOpen, onSelect }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="flex w-full items-center gap-1 pl-4 pr-2 py-0.5 text-[10px] text-gray-400 hover:bg-gray-50"
      >
        {isOpen ? <ChevronDownIcon className="w-2.5 h-2.5" /> : <ChevronRightIcon className="w-2.5 h-2.5" />}
        {continent ?? 'Other'}
      </button>
      {isOpen &&
        options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className={`w-full truncate text-left pl-7 pr-4 py-1 text-xs hover:bg-gray-100 ${selectedValue === opt.value ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
          >
            {opt.label}
          </button>
        ))}
    </div>
  );
}

ContinentSubsection.propTypes = {
  continent: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedValue: PropTypes.string,
  defaultOpen: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function VersionDropdownSection({ label, continents, selectedValue, defaultOpen, onSelect }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="flex w-full items-center gap-1 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-400 hover:bg-gray-50"
      >
        {isOpen ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
        {label}
      </button>
      {isOpen &&
        continents.map(({ continent, options, defaultOpen: continentOpen }) => (
          <ContinentSubsection
            key={continent}
            continent={continent}
            options={options}
            selectedValue={selectedValue}
            defaultOpen={continentOpen}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

VersionDropdownSection.propTypes = {
  label: PropTypes.string.isRequired,
  continents: PropTypes.arrayOf(
    PropTypes.shape({
      continent: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ).isRequired,
      defaultOpen: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  selectedValue: PropTypes.string,
  defaultOpen: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const DataConfigurationField = () => {
  const { pathwayId, caseId, nodeId } = useParams();
  const isDefault = useIsDefault();
  const caseNode = useCaseNode();
  const availableSourcesByDatasetVersion = useMemo(
    () =>
      caseNode?.available_sources_by_dataset_version?.length
        ? caseNode.available_sources_by_dataset_version
        : caseNode?.available_sources?.length
          ? [
              {
                dataset_version: null,
                is_latest: false,
                continents: [{ continent: null, sources: caseNode.available_sources }],
              },
            ]
          : [],
    [caseNode?.available_sources, caseNode?.available_sources_by_dataset_version],
  );
  const availableConfigurations = useMemo(
    () => caseNode?.available_configurations || [],
    [caseNode?.available_configurations],
  );
  const paramSource = caseNode?.params_source;
  const dropdownRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sourceOptionGroups = useMemo(
    () =>
      availableSourcesByDatasetVersion.map(({ dataset_version, continents, is_latest }) => {
        const continentGroups = continents.map(({ continent, sources }) => ({
          continent,
          options: sources.map(source => ({ value: source, label: source })),
          defaultOpen: !!paramSource && sources.includes(paramSource),
        }));
        const versionHasSelected = continentGroups.some(c => c.defaultOpen);
        return {
          label: dataset_version ?? 'Default',
          continents: continentGroups,
          defaultOpen: versionHasSelected || (!paramSource && is_latest),
        };
      }),
    [availableSourcesByDatasetVersion, paramSource],
  );

  const configOptions = useMemo(
    () =>
      availableConfigurations.map(config => ({
        value: `config:${config.id}`,
        label: config.name,
      })),
    [availableConfigurations],
  );

  const allFlatOptions = useMemo(
    () => [...sourceOptionGroups.flatMap(g => g.continents.flatMap(c => c.options)), ...configOptions],
    [sourceOptionGroups, configOptions],
  );

  const fromLabel = allFlatOptions.find(opt => opt.value === paramSource)?.label || paramSource;
  const toLabel = allFlatOptions.find(opt => opt.value === pendingValue)?.label || pendingValue;

  const handleChange = e => {
    const newValue = e.target.value;
    if (newValue !== paramSource) {
      setPendingValue(newValue);
      setShowModal(true);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setPendingValue(null);
  };

  const handleContinue = async resetParams => {
    setIsLoading(true);
    try {
      await patchNode(caseId, nodeId, {
        params_source: pendingValue,
        reset_params: resetParams,
      });
      await nodeActions.init(pathwayId, caseId, nodeId);
      await caseActions.recheckBalanceAndAnalysis();
      setShowModal(false);
      setPendingValue(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfiguration = async configId => {
    await deleteParamConfiguration(configId, { suppressErrorToast: true });
    await nodeActions.init(pathwayId, caseId, nodeId);
  };

  const handleSaveConfiguration = async ({ name, permissions }) => {
    setIsSaving(true);
    try {
      const { data: newConfig } = await createParamConfiguration({
        case_id: caseId,
        node_id: nodeId,
        name,
        permissions,
      });
      await patchNode(caseId, nodeId, {
        params_source: `config:${newConfig.id}`,
        reset_params: true,
      });
      await nodeActions.init(pathwayId, caseId, nodeId);
      setShowSaveModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditConfiguration = async (configId, { name, permissions }) => {
    await updateParamConfiguration(configId, { name, permissions });
    await nodeActions.init(pathwayId, caseId, nodeId);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Tooltip
          label="Data Configuration"
          text="Choose the parameter dataset used for this module. **Default** uses Sesame's curated values. Switch to a saved configuration to reuse a custom set of parameter values, or modify parameters below and save them as a new named configuration for use across pathways."
        >
          <CircleStackIcon className="w-4 h-4 text-purple-500 shrink-0" />
        </Tooltip>
        <details className="dropdown" ref={dropdownRef}>
          <summary className="list-none flex cursor-pointer items-center gap-0.5 rounded px-1 py-0.5 text-xs hover:bg-base-200 max-w-[175px]">
            <span className="truncate">{fromLabel || paramSource || '—'}</span>
            <ChevronDownIcon className="w-3 h-3 shrink-0 text-gray-400" />
          </summary>
          <div className="dropdown-content z-[100] mt-1 w-80 max-h-96 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
            {sourceOptionGroups.map(group => (
              <VersionDropdownSection
                key={group.label}
                label={group.label}
                continents={group.continents}
                selectedValue={paramSource}
                defaultOpen={group.defaultOpen}
                onSelect={value => {
                  handleChange({ target: { value } });
                  if (dropdownRef.current) dropdownRef.current.open = false;
                }}
              />
            ))}
            {configOptions.length > 0 && (
              <>
                <div className="mx-2 my-1 border-t border-gray-100" />
                <div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Saved configurations
                </div>
                {configOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      handleChange({ target: { value: opt.value } });
                      if (dropdownRef.current) dropdownRef.current.open = false;
                    }}
                    className={`w-full truncate text-left px-4 py-1 text-xs hover:bg-gray-100 ${paramSource === opt.value ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </>
            )}
          </div>
        </details>
        {!isDefault && (
          <button
            type="button"
            onClick={() => setShowSaveModal(true)}
            className="btn btn-ghost btn-xs btn-circle"
            title="Save configuration"
          >
            <FloppyDiskIcon className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowManageModal(true)}
          className="btn btn-ghost btn-xs btn-circle"
          title="Manage configurations"
        >
          <Cog6ToothIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      <ConfigurationChangeModal
        show={showModal}
        fromValue={fromLabel}
        toValue={toLabel}
        isDefault={isDefault}
        onCancel={handleCancel}
        onContinue={handleContinue}
        isLoading={isLoading}
      />
      {showSaveModal && (
        <SaveConfigurationModal
          key="create-configuration-modal"
          show
          onCancel={() => setShowSaveModal(false)}
          onSave={handleSaveConfiguration}
          isLoading={isSaving}
        />
      )}
      <ManageConfigurationsModal
        show={showManageModal}
        availableSourcesByDatasetVersion={availableSourcesByDatasetVersion}
        availableConfigurations={availableConfigurations}
        onEdit={handleEditConfiguration}
        onDelete={handleDeleteConfiguration}
        onClose={() => setShowManageModal(false)}
      />
    </>
  );
};

export default DataConfigurationField;
